import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ObligationDTO, PersonDTO } from '@almanac/shared';
import { LoadStatus } from '../components/LoadStatus.js';
import { PersonAvatar } from '../components/PersonAvatar.js';
import { PersonCard } from '../components/PersonCard.js';
import { PersonEditSheet } from '../components/PersonEditSheet.js';
import { useDocuments } from '../lib/documents.js';
import { useCurrentUser } from '../lib/me.js';
import { useObligations } from '../lib/obligations.js';
import {
  isHouseholdMember,
  personDocuments,
  personOccasions,
  shortOccasionLabel,
  usePeople,
} from '../lib/people.js';

/**
 * The People page — the findable home for everyone the hub knows about
 * (Documents' "who it's about", Obligations' birthdays/anniversaries).
 * Same chassis as the Documents index: header with back + search, an
 * add row, then one row per person. Tapping a row opens the read-first
 * person card; editing happens only through the shared edit sheet.
 */
export function PeoplePage() {
  const people = usePeople();
  const obligations = useObligations();
  const documents = useDocuments();
  const me = useCurrentUser();
  const navigate = useNavigate();

  const [previewId, setPreviewId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  function closeSearch() {
    setSearchOpen(false);
    setQuery('');
  }

  const all = useMemo(
    () =>
      [...(people.data ?? [])].sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [people.data],
  );

  const trimmed = query.trim().toLowerCase();
  const filtered = trimmed
    ? all.filter(
        (p) =>
          p.displayName.toLowerCase().includes(trimmed) ||
          (p.relationship ?? '').toLowerCase().includes(trimmed),
      )
    : all;

  // Household members (the explicit flag, plus your own linked record)
  // float into their own group; everyone else sits under Others. Purely
  // presentational — with no household members it's one flat list.
  const meId = me.data?.personId;
  const household = filtered.filter((p) => isHouseholdMember(p, meId));
  const others = filtered.filter((p) => !isHouseholdMember(p, meId));
  const grouped: { label: string | null; members: PersonDTO[] }[] =
    household.length > 0
      ? [
          { label: 'Household', members: household },
          { label: 'Others', members: others },
        ].filter((g) => g.members.length > 0)
      : [{ label: null, members: others }];

  function rowMeta(p: PersonDTO): string {
    const next = personOccasions(obligations.data ?? [], p.id)[0];
    return [p.relationship, next ? shortOccasionLabel(next) : null]
      .filter(Boolean)
      .join(' · ');
  }

  // Desktop rail: the next few birthdays/anniversaries across everyone,
  // each opening its person's card, plus headline counts. Counts run
  // over the full list, not the search-filtered view.
  const upcoming = useMemo(() => {
    const occasions = (obligations.data ?? [])
      .filter((o) => o.kind === 'occasion' && o.status !== 'completed')
      .sort((a, b) =>
        (a.nextDueDate ?? a.dueDate).localeCompare(b.nextDueDate ?? b.dueDate),
      );
    const out: { occasion: ObligationDTO; person: PersonDTO }[] = [];
    for (const o of occasions) {
      const person = (people.data ?? []).find((p) => o.personIds.includes(p.id));
      if (person) out.push({ occasion: o, person });
      if (out.length === 5) break;
    }
    return out;
  }, [obligations.data, people.data]);
  const householdCount = useMemo(
    () => all.filter((p) => isHouseholdMember(p, meId)).length,
    [all, meId],
  );
  const withDocsCount = useMemo(
    () => all.filter((p) => personDocuments(documents.data ?? [], p.id).length > 0).length,
    [all, documents.data],
  );

  function rowDocs(p: PersonDTO): string {
    const n = personDocuments(documents.data ?? [], p.id).length;
    return n === 0 ? '' : n === 1 ? '1 doc' : `${n} docs`;
  }

  return (
    <section>
      <header className="page-header page-header--with-action">
        <div className="page-header__lead">
          <button
            type="button"
            className="page-header__icon-button page-header__back"
            aria-label="Back to home"
            onClick={() => navigate('/')}
          >
            <BackIcon />
          </button>
          <div>
            <div className="section__label">Everyone</div>
            <h2 className="page-header__title">People</h2>
          </div>
        </div>
        <button
          type="button"
          className={`page-header__icon-button ${searchOpen ? 'page-header__icon-button--active' : ''}`}
          onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
          aria-label={searchOpen ? 'Close search' : 'Search people'}
          aria-expanded={searchOpen}
        >
          <SearchIcon />
        </button>
      </header>

      {searchOpen && (
        <div className="obligations-search">
          <input
            ref={searchInputRef}
            type="search"
            className="obligations-search__input"
            placeholder="Search by name or relationship…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') closeSearch();
            }}
            aria-label="Search people"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <button
            type="button"
            className="obligations-search__clear"
            onClick={closeSearch}
            aria-label="Close search"
          >
            ×
          </button>
        </div>
      )}

      <LoadStatus isLoading={people.isLoading} isError={people.isError} error={people.error} />

      <div className="page-columns">
      <div className="page-measure">
      <button type="button" className="documents-add" onClick={() => setCreating(true)}>
        <span className="documents-add__plus" aria-hidden>
          +
        </span>
        Add a person
      </button>

      {people.isSuccess && all.length === 0 && (
        <p className="empty-state">
          No people yet. Add someone here, or just type a name when recording a birthday or
          tagging a document — they&rsquo;ll appear in this list.
        </p>
      )}

      {people.isSuccess && all.length > 0 && filtered.length === 0 && (
        <p className="empty-state">No one matches &ldquo;{query.trim()}&rdquo;.</p>
      )}

      {grouped.map((group) => (
        <div key={group.label ?? 'all'}>
          {group.label && <div className="doc-seclabel">{group.label}</div>}
          <div className="doc-folders">
            {group.members.map((p) => {
              const meta = rowMeta(p);
              const docs = rowDocs(p);
              return (
                <button
                  key={p.id}
                  type="button"
                  className="doc-folder"
                  onClick={() => setPreviewId(p.id)}
                  aria-label={`Open ${p.displayName}`}
                >
                  <PersonAvatar person={p} className="doc-folder__av doc-folder__av--person" />
                  <span className="people-row__mid">
                    <span className="doc-folder__name">
                      {p.displayName}
                      {me.data?.personId === p.id && <span className="shared-pill">You</span>}
                    </span>
                    {meta && <span className="doc-row__meta">{meta}</span>}
                  </span>
                  {docs && <span className="people-row__docs">{docs}</span>}
                  <ChevronRight />
                </button>
              );
            })}
          </div>
        </div>
      ))}

      </div>

      {/* Desktop-only rail (display:none below 840px): the next few
          occasions across everyone, and headline counts. */}
      <aside className="page-rail">
        {upcoming.length > 0 && (
          <div className="rail-card">
            <div className="doc-seclabel">Coming up</div>
            {upcoming.map(({ occasion, person }) => (
              <button
                key={occasion.id}
                type="button"
                className="doc-folder"
                onClick={() => setPreviewId(person.id)}
                aria-label={`Open ${person.displayName}`}
              >
                <PersonAvatar
                  person={person}
                  className="doc-folder__av doc-folder__av--person"
                />
                <span className="people-row__mid">
                  <span className="doc-folder__name">{person.displayName}</span>
                  <span className="doc-row__meta">{shortOccasionLabel(occasion)}</span>
                </span>
              </button>
            ))}
          </div>
        )}

        {people.isSuccess && all.length > 0 && (
          <div className="rail-card">
            <div className="doc-seclabel">At a glance</div>
            <div className="rail-glance">
              <div className="rail-glance__line">
                <strong>{all.length}</strong> {all.length === 1 ? 'person' : 'people'}
              </div>
              {householdCount > 0 && (
                <div className="rail-glance__line">
                  <strong>{householdCount}</strong> in the household
                </div>
              )}
              {withDocsCount > 0 && (
                <div className="rail-glance__line">
                  <strong>{withDocsCount}</strong> with documents
                </div>
              )}
            </div>
          </div>
        )}
      </aside>
      </div>

      {previewId && <PersonCard personId={previewId} onClose={() => setPreviewId(null)} />}
      {creating && <PersonEditSheet target="new" onClose={() => setCreating(false)} />}
    </section>
  );
}

function BackIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 18l-6-6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden>
      <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M13 13l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function ChevronRight() {
  return (
    <svg
      className="doc-folder__chev"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
